import React, { useMemo } from 'react';
import { useSettingsStore } from '../../store/settingsStore';
import './CandleMetricsInfoOverlay.css';

interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CandleMetricsInfoOverlayProps {
  candle: Candle | null;
  atrValue: number;
}

const CandleMetricsInfoOverlay: React.FC<CandleMetricsInfoOverlayProps> = React.memo(({ candle, atrValue }) => {
  const settings = useSettingsStore((state) => state.wholeCandle);

  if (!candle) {
    return null;
  }

  // Calculate candle metrics - memoized to avoid recalculation on every render
  const metrics = useMemo(() => {
    const candleRange = candle.high - candle.low;
    const upperWick = candle.high - Math.max(candle.open, candle.close);
    const lowerWick = Math.min(candle.open, candle.close) - candle.low;
    const combinedWicks = upperWick + lowerWick;
    const wickPercentage = candleRange > 0 ? (combinedWicks / candleRange) * 100 : 0;
    const isWithinThreshold = wickPercentage >= settings.wickThresholdMin && wickPercentage <= settings.wickThresholdMax;
    const thresholdText = isWithinThreshold
      ? `< ${settings.wickThresholdMax}%`
      : `> ${settings.wickThresholdMax}%`;

    return {
      candleRange,
      combinedWicks,
      wickPercentage,
      isWithinThreshold,
      thresholdText
    };
  }, [candle.high, candle.low, candle.open, candle.close, settings.wickThresholdMin, settings.wickThresholdMax]);

  return (
    <div className="candle-metrics-info-overlay">
      <div className="candle-metrics-info-content">
        <div className="candle-metrics-info-item">
          <span className="candle-metrics-info-label">RANGE:</span>
          <span className="candle-metrics-info-value range">{metrics.candleRange.toFixed(2)}</span>
        </div>
        <div className="candle-metrics-info-item">
          <span className="candle-metrics-info-label">ATR:</span>
          <span className="candle-metrics-info-value atr">{atrValue.toFixed(2)}</span>
        </div>
        <div className="candle-metrics-info-item">
          <span className="candle-metrics-info-label">WICKS:</span>
          <span className="candle-metrics-info-value wicks">{metrics.combinedWicks.toFixed(2)}</span>
        </div>
        <div className="candle-metrics-info-item">
          <span className="candle-metrics-info-label">WICK%:</span>
          <span className={`candle-metrics-info-value wick-percent ${metrics.isWithinThreshold ? 'within' : 'outside'}`}>
            {metrics.wickPercentage.toFixed(1)}%
          </span>
        </div>
        <div className="candle-metrics-info-separator">|</div>
        <div className="candle-metrics-info-item">
          <span className={`candle-metrics-info-value threshold ${metrics.isWithinThreshold ? 'within' : 'outside'}`}>
            {metrics.thresholdText}
          </span>
        </div>
      </div>
    </div>
  );
});

export default CandleMetricsInfoOverlay;
